/* eslint-disable no-mixed-spaces-and-tabs */
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro-6";
import React from "react";
import { useTranslation } from "react-i18next";

import Highlight from "../../../../components/Highlight";
import { formatDate, formatDurationInHoursAndMinutes, getLeaveTypeNames, hasPermission } from "../../../../utils/helpers";
import useWorkerRequestsData from "../../controllers/use-worker-requests-data";
import { RegularShiftRequest } from "../../store/worker-requests-types";
import Actions from "../table/actions";
import Badge from "../table/badge";
import CoverageTag from "../table/coverage-tag";

const useFullTimeRequestsColumns = (
  searchText: string,
  handleEditClick: ((request: RegularShiftRequest) => void) | undefined,
  handleViewCallDetail?: (request: RegularShiftRequest) => void
) => {
  const { t } = useTranslation();
  const { attendanceManagement, userRolesWithPermission, callOffLineEnabledLocations } =
    useWorkerRequestsData();
  const canEdit = hasPermission(
    userRolesWithPermission,
    "WORKER_REQUESTS.EDIT_REGULAR_SHIFT_REQUEST"
  );
  const canAcceptReject = hasPermission(
    userRolesWithPermission,
    "WORKER_REQUESTS.ACCEPT_REJECT_REGULAR_REQUEST"
  );
  // Recording/transcript access requires call-off line to be enabled for a
  // location this user can access; an empty list means no access at all.
  const isCallOffLineEnabled = (callOffLineEnabledLocations?.length ?? 0) > 0;

  const columns: GridColDef[] = [
    {
      field: "requestDate",
      type: "date",
      minWidth: 185,
      flex: 1,
      headerName: t("worker-requests_requested-on"),
      renderHeader: () => (
        <span style={{ padding: "0px 4px", fontWeight: "bold" }}>
          {t("worker-requests_requested-on")}
        </span>
      ),
      renderCell: (params) => {
        const date = `${formatDate({
          dateStr: params.row["requestDate"],
          formatType: "NUMERIC_DATE",
        })} ${formatDate({
          dateStr: params.row["requestDate"],
          formatType: "TIME",
        })}`;
        return (
          <Box p={1} sx={{ wordBreak: "break-word" }}>
            <Typography
              sx={{
                textOverflow: "ellipsis",
                width: "100%",
                overflow: "hidden",
                padding: "0px 4px",
                fontStyle: "roboto",
                fontWeight: "400",
                fontSize: "14px",
              }}
            >
              <Highlight value={date} searchText={searchText} />
            </Typography>
          </Box>
        );
      },
      valueGetter: (params) => new Date(params.row["requestDate"]),
    },
    {
      field: "shiftDetails",
      type: "date",
      minWidth: 240,
      flex: 1,
      headerName: t("worker-requests_shift-details"),
      renderHeader: () => (
        <span style={{ padding: "0px 4px", fontWeight: "bold" }}>
          {t("worker-requests_shift-details")}
        </span>
      ),
      valueGetter: (params) => new Date(params?.row?.ftShift["startDateTime"]),
      renderCell: (params) => {
        const ftShift = params?.row?.ftShift;
        if (!ftShift) return <Box ml={5}>-</Box>;
        const date = formatDate({
          dateStr: ftShift["startDateTime"],
          formatType: "NUMERIC_DATE",
        });
        const name = ftShift["patternName"]
          ? "(" + ftShift["patternName"] + ")"
          : "";
        const time = formatDate({
          dateStr: ftShift["startDateTime"],
          formatType: "TIME_RANGE",
          endDateStr: ftShift["endDateTime"],
        });
        const value = `${date} ${time}`;

        return (
          <Box sx={{ wordBreak: "break-word" }}>
            <Tooltip title={value} disableInteractive>
              <Typography
                sx={{
                  textOverflow: "ellipsis",
                  width: "100%",
                  overflow: "hidden",
                  padding: "0px 4px",
                  fontStyle: "roboto",
                  fontWeight: "400",
                  fontSize: "14px",
                }}
              >
                <Highlight value={value} searchText={searchText} />{" "}
                <span
                  style={{
                    display: "inline-block",
                  }}
                >
                  <Highlight value={name} searchText={searchText} />
                </span>
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "workCenterName",
      type: "string",
      minWidth: 155,
      flex: 1,
      headerName: t("worker-requests_work-center"),
      renderHeader: () => (
        <span style={{ padding: "0px 4px", fontWeight: "bold" }}>
          {t("worker-requests_work-center")}
        </span>
      ),
      renderCell: (params) => {
        const workCenterName = params?.row?.worker["workCenterName"] ?? "-";
        return (
          <Tooltip title={workCenterName} disableInteractive>
            <Typography
              sx={{
                textOverflow: "ellipsis",
                width: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                padding: "0px 4px",
                fontStyle: "roboto",
                fontWeight: "400",
                fontSize: "14px",
              }}
            >
              <Highlight value={workCenterName} searchText={searchText} />
            </Typography>
          </Tooltip>
        );
      },
      valueGetter: (params) => params?.row?.worker["workCenterName"],
    },
    {
      field: "workerName",
      type: "string",
      minWidth: 155,
      flex: 1,
      headerName: t("worker-requests_worker-full-name"),
      renderHeader: () => (
        <span style={{ padding: "0px 4px", fontWeight: "bold" }}>
          {t("worker-requests_worker-full-name")}
        </span>
      ),
      renderCell: (params) => {
        const workerName =
          params?.row?.worker["firstName"] +
          " " +
          params?.row?.worker["lastName"];
        return (
          <Tooltip title={workerName} disableInteractive>
            <Typography
              sx={{
                textOverflow: "ellipsis",
                width: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                padding: "0px 4px",
                fontStyle: "roboto",
                fontWeight: "400",
                fontSize: "14px",
              }}
            >
              <Highlight value={workerName} searchText={searchText} />
            </Typography>
          </Tooltip>
        );
      },
      valueGetter: (params) =>
        params?.row?.worker["firstName"] +
        " " +
        params?.row?.worker["lastName"],
    },
    {
      field: "requestType",
      type: "string",
      minWidth: 200,
      flex: 1.5,
      headerName: t("worker-requests_absence-type"),
      renderHeader: () => (
        <span style={{ padding: "0px 4px", fontWeight: "bold" }}>
          {t("worker-requests_absence-type")}
        </span>
      ),
      valueGetter: (params) => params.row.requestType?.name,
      renderCell: (params) => {
        const startDate = params.row["startDate"];
        const endDate = params.row["endDate"];

        const timeOffString = formatDurationInHoursAndMinutes(
          startDate,
          endDate
        );

        const requestTypeName = params.row.requestType?.name ?? "-";
        const requestType = `${requestTypeName} (${timeOffString})`;
        return (
          <Box>
            <Tooltip title={requestType} disableInteractive>
              <Typography
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "14px",
                  padding: "0px 4px",
                  wordBreak: "break-word",
                }}
              >
                <Highlight value={requestType} searchText={searchText} />
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "comment",
      type: "string",
      headerName: t("worker-requests_reason-for-absence"),
      flex: 1,
      minWidth: 200,
      renderHeader: () => (
        <span style={{ padding: "0px 12px 0px 8px", fontWeight: "bold" }}>
          {t("worker-requests_reason-for-absence")}
        </span>
      ),
      renderCell: (params) => (
        <Box p={1} sx={{ wordBreak: "break-word" }}>
          <Tooltip title={params?.row?.workerComment} disableInteractive>
            <Typography
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
                fontStyle: "roboto",
                fontWeight: "400",
                fontSize: "14px",
              }}
            >
              <Highlight
                value={
                  params?.row?.workerComment ? params?.row?.workerComment : "-"
                }
                searchText={searchText}
              />
            </Typography>
          </Tooltip>
        </Box>
      ),
      valueGetter: (params) => params?.row?.workerComment,
    },
    {
      field: "actionTakenBy",
      type: "string",
      minWidth: 155,
      flex: 1,
      headerName: t("worker-requests_updated-by"),
      renderHeader: () => (
        <span style={{ padding: "0px 4px", fontWeight: "bold" }}>
          {t("worker-requests_updated-by")}
        </span>
      ),
      renderCell: (params) => {
        const name =
          params?.row?.status?.code === "aa"
            ? t("worker-requests_system-rules")
            : params?.row?.actionTakenBy?.fullName ?? "-";
        return (
          <Tooltip title={name} disableInteractive>
            <Typography
              sx={{
                textOverflow: "ellipsis",
                width: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                padding: "0px 4px",
                fontStyle: "roboto",
                fontWeight: "400",
                fontSize: "14px",
              }}
            >
              <Highlight value={name} searchText={searchText} />
            </Typography>
          </Tooltip>
        );
      },
      valueGetter: (params) => params?.row?.actionTakenBy?.fullName,
    },
    {
      field: "status",
      type: "string",
      minWidth: 150,
      flex: 1,
      headerName: t("worker-requests_request-status"),
      renderHeader: () => (
        <span style={{ padding: "0px 4px", fontWeight: "bold" }}>
          {t("worker-requests_request-status")}
        </span>
      ),
      renderCell: (params) => {
        return (
          <Stack direction={"column"} spacing={1.5} sx={{ width: "100%" }}>
            <Badge
              statusCode={params.row.status["code"]}
              statusName={params.row.status["name"]}
              requestSubStatusCode={params.row.requestSubStatus?.code}
            />
            {params.row?.status?.code === "a" ||
            params.row?.status?.code === "aa" ? (
              <CoverageTag isPublished={params.row?.flexShiftIds ?? false} />
            ) : null}
          </Stack>
        );
      },
      valueGetter: (params) => params.row.status["name"],
    },
    {
      field: "actions",
      type: "string",
      minWidth: canEdit ? 129 : 86,
      maxWidth: canEdit ? 129 : 86,
      width: canEdit ? 129 : 86,
      headerName: t("common_actions"),
      cellClassName: "actions-col-cell",
      headerClassName: "actions-col-header",
      sortable: false,
      resizable: false,
      renderHeader: () => (
        <Box
          sx={{
            width: canEdit ? "129px" : "86px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              padding: "0px 4px",
              fontWeight: "bold",
            }}
          >
            {t("common_actions")}
          </span>
        </Box>
      ),
      renderCell: (params) => {
        const openCreateNewShiftDialog = params.row.openCreateNewShiftDialog;
        const openShiftDetailsDialog = params.row.openShiftDetailsDialog;
        const date = params.row["startDate"];
        const statusCode = params.row.status["code"];
        const formData = {
          requestId: params.row["requestId"],
          ftShiftId: params.row["ftShiftId"],
          comment: params.row["supervisorComment"] ?? "",
        };

        const requestData = {
          name:
            params.row.worker["firstName"] +
            " " +
            params.row.worker["lastName"],
          requestType: params.row.requestType.name,
          requestCode: params.row.requestType.code,
          leaveType: getLeaveTypeNames(params.row.leaveTypes).join(", ") ?? "",
          startDateTime: params.row["startDate"],
          endDateTime: params.row["endDate"],
          workerComment: params.row.workerComment,
          shiftDetails: params.row["ftShift"].name ?? "",
          requestStatusCode: statusCode,
          requestSubStatusCode: params.row.requestSubStatus?.code,
          flexShiftIds: params.row["flexShiftIds"] ?? null,
          requestId: params.row["requestId"],
          callOffLineEnabled: params.row["callOffLineEnabled"] ?? false,
          conversationIds: params.row["conversationIds"] ?? [],
          requestDate: params.row["requestDate"],
        };

        return (
          <Actions
            openCreateNewShiftDialog={openCreateNewShiftDialog}
            date={date}
            formData={formData}
            requestData={requestData}
            statusCode={statusCode}
            openShiftDetailsDialog={openShiftDetailsDialog}
            id={params.row.id}
            canEdit={canEdit}
            canAcceptReject={canAcceptReject}
            handleEditClick={() =>
              handleEditClick ? handleEditClick(params.row) : null
            }
            canViewCallDetail={
              isCallOffLineEnabled &&
              Boolean(params.row.callOffLineEnabled) &&
              (params.row.conversationIds?.length ?? 0) > 0
            }
            handleViewCallDetail={() =>
              handleViewCallDetail ? handleViewCallDetail(params.row) : null
            }
          />
        );
      },
    },
  ];
  const timeOffType: GridColDef = {
    field: "leaveType",
    type: "string",
    minWidth: 200,
    flex: 1.5,
    headerName: t("worker-requests_time-off-type-used"),
    renderHeader: () => (
      <span style={{ padding: "0px 4px", fontWeight: "bold" }}>
        {t("worker-requests_time-off-type-used")}
      </span>
    ),
    valueGetter: (params) => params.row.leaveType?.name,
    renderCell: (params) => {
      const leaveTypes = params.row.leaveTypes;
      const leaveTypeNames = getLeaveTypeNames(leaveTypes);

      if (leaveTypeNames.length === 0) {
        return "-";
      }

      return (
        // This outer Box handles vertical overflow if there are too many lines for the cell height.
        <Box
          sx={{
            width: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            paddingLeft: 0.5,
          }}
        >
          {leaveTypeNames.map((name, index) => {
            const displayText = name || "-";
            return (
              <Tooltip key={index} title={displayText} disableInteractive>
                {/* This inner Box ensures each name is on its own line and handles its own ellipsis */}
                <Box
                  sx={{
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Highlight value={displayText} searchText={searchText} />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      );
    },
  };
  if (attendanceManagement) columns.splice(4, 0, timeOffType);
  // Keep the action column when the user can accept/reject, edit, or access
  // call recordings & transcripts. ReadOnly users with call-off line access
  // still see the column (call icon only). Remove it when none apply.
  if (!canAcceptReject && !canEdit && !isCallOffLineEnabled) {
    const actionColumnIndex = columns.findIndex(
      (column) => column.field === "actions"
    );
    if (actionColumnIndex !== -1) {
      columns.splice(actionColumnIndex, 1);
    }
  }
  return columns;
};

export default useFullTimeRequestsColumns;
